import React from 'react';
import { authFetch } from '../authFetch';
import { getFileServeUrl, getFileThumbnailUrl } from '../workspace';

type PreviewVariant = 'thumbnail' | 'serve';

interface UseWorkspaceImagePreviewOptions {
  fileId: string | null;
  variant?: PreviewVariant;
  enabled?: boolean;
  fallbackUrl?: string | null;
  allowServeFallback?: boolean;
}

interface PreviewCacheEntry {
  objectUrl: string | null;
  promise: Promise<string | null> | null;
  refCount: number;
  cleanupTimer: number | null;
}

const PREVIEW_CACHE_TTL_MS = 20_000;
// DOCX 업로드 응답은 원본 저장까지만 기다리고 LibreOffice 썸네일 렌더링은
// background task로 이어진다. 첫 404 직후 한 번(기존 180ms)만 재시도하면
// 보통 1~3초 걸리는 WebP 생성이 끝나기 전에 카드가 아이콘으로 굳어 새로고침
// 전에는 썸네일이 나타나지 않는다. 아래 값은 각 시도 사이의 상대 지연이다.
const THUMBNAIL_RETRY_DELAYS_MS = [0, 250, 750, 1_500, 3_000] as const;
const previewCache = new Map<string, PreviewCacheEntry>();

function buildCacheKey(fileId: string, variant: PreviewVariant): string {
  return `${variant}:${fileId}`;
}

function getAttemptPlan(fileId: string, variant: PreviewVariant, allowServeFallback: boolean): Array<{
  key: string;
  url: string;
  delayMs: number;
}> {
  if (variant === 'serve') {
    return [
      {
        key: buildCacheKey(fileId, 'serve'),
        url: getFileServeUrl(fileId),
        delayMs: 0,
      },
    ];
  }

  const thumbnailAttempts = THUMBNAIL_RETRY_DELAYS_MS.map((delayMs) => ({
    key: buildCacheKey(fileId, 'thumbnail'),
    url: getFileThumbnailUrl(fileId),
    delayMs,
  }));

  return [
    ...thumbnailAttempts,
    ...(allowServeFallback
      ? [{
          key: buildCacheKey(fileId, 'serve'),
          url: getFileServeUrl(fileId),
          delayMs: 0,
        }]
      : []),
  ];
}

function wait(delayMs: number, signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  if (delayMs <= 0) return Promise.resolve(true);

  return new Promise<boolean>((resolve) => {
    const finish = (ready: boolean) => {
      window.clearTimeout(timerId);
      signal.removeEventListener('abort', handleAbort);
      resolve(ready);
    };
    const handleAbort = () => finish(false);
    const timerId = window.setTimeout(() => finish(true), delayMs);
    signal.addEventListener('abort', handleAbort, { once: true });
  });
}

function scheduleEntryCleanup(cacheKey: string) {
  const entry = previewCache.get(cacheKey);
  if (!entry) return;
  if (entry.cleanupTimer !== null) {
    window.clearTimeout(entry.cleanupTimer);
  }
  entry.cleanupTimer = window.setTimeout(() => {
    const latest = previewCache.get(cacheKey);
    if (!latest || latest.refCount > 0) return;
    if (latest.objectUrl) {
      URL.revokeObjectURL(latest.objectUrl);
    }
    previewCache.delete(cacheKey);
  }, PREVIEW_CACHE_TTL_MS);
}

function retainPreview(cacheKey: string, url: string): Promise<string | null> {
  let entry = previewCache.get(cacheKey);
  if (!entry) {
    entry = {
      objectUrl: null,
      promise: null,
      refCount: 0,
      cleanupTimer: null,
    };
    previewCache.set(cacheKey, entry);
  }

  entry.refCount += 1;
  if (entry.cleanupTimer !== null) {
    window.clearTimeout(entry.cleanupTimer);
    entry.cleanupTimer = null;
  }

  if (entry.objectUrl) {
    return Promise.resolve(entry.objectUrl);
  }

  if (!entry.promise) {
    entry.promise = (async () => {
      const res = await authFetch(url);
      if (!res.ok) {
        throw new Error(`preview ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const latest = previewCache.get(cacheKey);
      if (!latest) {
        URL.revokeObjectURL(objectUrl);
        return null;
      }
      latest.objectUrl = objectUrl;
      latest.promise = null;
      return objectUrl;
    })().catch(() => {
      const latest = previewCache.get(cacheKey);
      if (latest) {
        latest.promise = null;
      }
      return null;
    });
  }

  return entry.promise;
}

function releasePreview(cacheKey: string) {
  const entry = previewCache.get(cacheKey);
  if (!entry) return;
  entry.refCount = Math.max(0, entry.refCount - 1);
  if (entry.refCount === 0) {
    scheduleEntryCleanup(cacheKey);
  }
}

export function useWorkspaceImagePreview({
  fileId,
  variant = 'thumbnail',
  enabled = true,
  fallbackUrl = null,
  allowServeFallback = false,
}: UseWorkspaceImagePreviewOptions): string | null {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(() => (
    fileId ? null : (fallbackUrl ?? null)
  ));

  React.useEffect(() => {
    if (!enabled) {
      setPreviewUrl(null);
      return;
    }

    if (!fileId) {
      setPreviewUrl(fallbackUrl ?? null);
      return;
    }

    let disposed = false;
    const retryController = new AbortController();
    const retainedKeyCounts = new Map<string, number>();
    setPreviewUrl(null);

    const load = async () => {
      const attempts = getAttemptPlan(fileId, variant, allowServeFallback);
      for (const attempt of attempts) {
        const ready = await wait(attempt.delayMs, retryController.signal);
        if (!ready || disposed) return;
        // retainPreview는 await 전에 refCount를 올린다. Promise가 끝난 뒤에만
        // retainedKeyCounts를 기록하면 LibreOffice 썸네일 생성 도중 unmount된
        // 카드가 cleanup에서 누락되어 object URL/refCount가 영구 잔존한다.
        const pending = retainPreview(attempt.key, attempt.url);
        retainedKeyCounts.set(attempt.key, (retainedKeyCounts.get(attempt.key) ?? 0) + 1);
        const resolved = await pending;
        if (disposed) return;
        if (resolved) {
          setPreviewUrl(resolved);
          return;
        }
      }
      if (!disposed) {
        setPreviewUrl(fallbackUrl ?? null);
      }
    };

    void load();

    return () => {
      disposed = true;
      retryController.abort();
      retainedKeyCounts.forEach((count, cacheKey) => {
        for (let index = 0; index < count; index += 1) {
          releasePreview(cacheKey);
        }
      });
    };
  }, [allowServeFallback, enabled, fallbackUrl, fileId, variant]);

  return previewUrl ?? fallbackUrl ?? null;
}
